import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const initial = {
  nominee_name: "", nominee_company: "", nominee_role: "", nominee_industry: "",
  nominee_location: "", nominee_contact: "", why_featured: "", what_built: "",
  your_name: "", your_email: "", your_relationship: "",
};

export default function Nominate() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/nominations", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div data-testid="nominate-success" className="max-w-reading mx-auto px-6 py-32 text-center">
        <div className="eyebrow text-oc-coral mb-6">Received</div>
        <h1 className="font-editorial text-4xl md:text-5xl text-oc-ink leading-tight">
          Thank you for the nomination.
        </h1>
        <p className="mt-8 font-editorial text-lg text-oc-ink-soft leading-relaxed">
          We'll read your submission carefully. If your nominee is a fit for the editorial direction, we'll reach out to them personally. Recommendations from readers are how a lot of the best conversations we publish come to be.
        </p>
      </div>
    );

  return (
    <div data-testid="nominate-page">
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="eyebrow text-oc-coral mb-6">Nominate an Operator</div>
          <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
            Know someone whose experience deserves to be preserved?
          </h1>
          <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft max-w-3xl leading-snug">
            Nominate an operator, founder, owner, manager, or company builder whose lessons could help others think, lead, and operate better. We take reader nominations seriously, many of our best conversations start here.
          </p>
        </div>
      </section>

      <section className="max-w-reading mx-auto px-6 py-16">
        <form onSubmit={submit} className="space-y-8" data-testid="nominate-form">
          <div className="pb-8 border-b border-oc-hairline">
            <div className="eyebrow text-oc-coral mb-6">About the nominee</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Nominee name" required value={form.nominee_name} onChange={set("nominee_name")} testId="nom-name" />
              <Field label="Company" value={form.nominee_company} onChange={set("nominee_company")} testId="nom-company" />
              <Field label="Role" value={form.nominee_role} onChange={set("nominee_role")} testId="nom-role" />
              <Field label="Industry" value={form.nominee_industry} onChange={set("nominee_industry")} testId="nom-industry" />
              <Field label="Location" value={form.nominee_location} onChange={set("nominee_location")} testId="nom-location" />
              <Field label="Contact (if known)" value={form.nominee_contact} onChange={set("nominee_contact")} testId="nom-contact" placeholder="Email or LinkedIn" />
            </div>
            <div className="mt-5">
              <Textarea label="Why should they be featured?" required value={form.why_featured} onChange={set("why_featured")} testId="nom-why" rows={5} />
            </div>
            <div className="mt-5">
              <Textarea label="What have they built, led, repaired, improved, or sustained?" value={form.what_built} onChange={set("what_built")} testId="nom-built" rows={5} />
            </div>
          </div>

          <div className="pb-8 border-b border-oc-hairline">
            <div className="eyebrow text-oc-coral mb-6">About you</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name" required value={form.your_name} onChange={set("your_name")} testId="nom-your-name" />
              <Field label="Your email" required type="email" value={form.your_email} onChange={set("your_email")} testId="nom-your-email" />
              <Field label="Your relationship to nominee" value={form.your_relationship} onChange={set("your_relationship")} testId="nom-relationship" placeholder="e.g. former colleague, customer, admirer" />
            </div>
          </div>

          <button
            data-testid="nominate-submit"
            type="submit"
            disabled={submitting}
            className="h-14 px-10 bg-oc-coral text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-coral-hover disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Submit nomination"}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
      />
    </label>
  );
}
function Textarea({ label, value, onChange, required, rows = 4, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-editorial text-base leading-relaxed resize-y"
      />
    </label>
  );
}
